import { useEffect, useState, type ReactNode } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Dropdown } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { apiClient } from '@/lib/api-client'
import { toast } from 'react-hot-toast'

interface DeviceModelFormProps {
  isOpen: boolean
  modelId: number | null
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  name: string
  brand: string
  modelNumber: string
  description: string
  deviceType: 'chargeable' | 'non-chargeable' | 'plugin' | 'single-use'
  pluginType?: 'wall_socket' | 'usb'
  chargerAvailable: boolean
  batteryType?: 'AAA' | 'AA'
  batteryCount?: number
}

export function DeviceModelForm({ isOpen, modelId, onClose, onSuccess }: DeviceModelFormProps) {
  const [loading, setLoading] = useState(false)
  const [loadingModel, setLoadingModel] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      brand: '',
      modelNumber: '',
      description: '',
      deviceType: 'non-chargeable',
      chargerAvailable: false,
    },
  })

  const deviceType = watch('deviceType')

  useEffect(() => {
    if (!isOpen || !modelId) return
    let cancelled = false

    const load = async () => {
      setLoadingModel(true)
      try {
        const { data } = await apiClient.get(`/device-models/${modelId}`)
        if (cancelled) return
        reset({
          name: data.name || '',
          brand: data.brand || '',
          modelNumber: data.modelNumber || '',
          description: data.description || '',
          deviceType: data.deviceType || 'non-chargeable',
          pluginType: data.pluginType || undefined,
          chargerAvailable: !!data.chargerAvailable,
          batteryType: data.batteryType || undefined,
          batteryCount: data.batteryCount || undefined,
        })
      } catch {
        if (!cancelled) {
          toast.error('Failed to load model')
          onClose()
        }
      } finally {
        if (!cancelled) setLoadingModel(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [isOpen, modelId, reset])

  const onSubmit = async (data: FormData) => {
    if (!modelId) return
    setLoading(true)
    try {
      await apiClient.put(`/device-models/${modelId}`, {
        name: data.name.trim(),
        brand: data.brand.trim(),
        modelNumber: data.modelNumber.trim(),
        description: data.description.trim(),
        deviceType: data.deviceType,
        pluginType: data.deviceType === 'plugin' ? data.pluginType : null,
        chargerAvailable: data.deviceType === 'chargeable' ? data.chargerAvailable : false,
        batteryType: data.deviceType === 'non-chargeable' ? data.batteryType : null,
        batteryCount: data.deviceType === 'non-chargeable' ? data.batteryCount : null,
      })
      toast.success('Model updated')
      onSuccess()
      onClose()
    } catch (error: any) {
      const body = error.response?.data
      toast.error(body?.errors?.[0]?.msg || body?.message || 'Failed to update model')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit model" size="lg">
      {loadingModel ? (
        <div className="flex h-40 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <p className="text-sm text-muted-foreground">
            These details are shared by every asset of this model. Serial numbers, charger IDs, and assignments stay on each asset.
          </p>

          <Field label="Model name">
            <Input
              {...register('name', {
                required: 'Model name is required',
                minLength: { value: 2, message: 'Use at least 2 characters' },
              })}
              placeholder="HP EliteBook 840"
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Brand">
              <Input {...register('brand')} placeholder="HP" />
            </Field>
            <Field label="Model number">
              <Input {...register('modelNumber')} placeholder="840 G9" />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              {...register('description')}
              rows={3}
              placeholder="Optional notes about this model"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </Field>

          <Field label="Power type">
            <Controller
              name="deviceType"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  options={[
                    { value: 'chargeable', label: 'Chargeable' },
                    { value: 'non-chargeable', label: 'Non-chargeable' },
                    { value: 'plugin', label: 'Plugin' },
                    { value: 'single-use', label: 'Single use' },
                  ]}
                />
              )}
            />
          </Field>

          {deviceType === 'plugin' && (
            <Field label="Plugin connection">
              <Controller
                name="pluginType"
                control={control}
                rules={{ required: 'Select a connection' }}
                render={({ field }) => (
                  <Dropdown
                    value={field.value || ''}
                    placeholder="Select connection"
                    onChange={(value) => field.onChange(value)}
                    options={[
                      { value: 'wall_socket', label: 'Wall socket' },
                      { value: 'usb', label: 'USB' },
                    ]}
                  />
                )}
              />
              {errors.pluginType && <p className="text-sm text-destructive">Select a connection</p>}
            </Field>
          )}

          {deviceType === 'chargeable' && (
            <Controller
              name="chargerAvailable"
              control={control}
              render={({ field }) => (
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!field.value}
                  onClick={() => field.onChange(!field.value)}
                  className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3 text-left text-sm"
                >
                  <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${field.value ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                    <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${field.value ? 'translate-x-5' : 'translate-x-0'}`} />
                  </span>
                  Charger included with this model
                </button>
              )}
            />
          )}

          {deviceType === 'single-use' && (
            <p className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground">
              Inbuilt battery. Not rechargeable. Mark each asset EoL when it is finished.
            </p>
          )}

          {deviceType === 'non-chargeable' && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Battery type">
                <Controller
                  name="batteryType"
                  control={control}
                  rules={{ required: 'Select a battery type' }}
                  render={({ field }) => (
                    <Dropdown
                      value={field.value || ''}
                      placeholder="Select type"
                      onChange={(value) => field.onChange(value)}
                      options={[
                        { value: 'AAA', label: 'AAA' },
                        { value: 'AA', label: 'AA' },
                      ]}
                    />
                  )}
                />
                {errors.batteryType && <p className="text-sm text-destructive">Select a battery type</p>}
              </Field>
              <Field label="Battery count">
                <Input
                  type="number"
                  min={1}
                  max={10}
                  {...register('batteryCount', {
                    required: 'Battery count is required',
                    valueAsNumber: true,
                    min: { value: 1, message: 'At least 1' },
                    max: { value: 10, message: 'At most 10' },
                  })}
                />
                {errors.batteryCount && (
                  <p className="text-sm text-destructive">{errors.batteryCount.message}</p>
                )}
              </Field>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save model'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium">{label}</label>
      {children}
    </div>
  )
}
